import { PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { Avatar } from '../../Avatar';
import { formatCreatedDate } from '../../../utils/dates';
import type { PatientNote } from '../../../services/patient/notes';
import { canManageNote } from './noteAccess';
import { NoteBody } from './NoteBody';
import { PinIcon } from './PinIcon';

interface NoteCardProps {
    note: PatientNote;
    onEdit: (note: PatientNote) => void;
    onDelete: (note: PatientNote) => void;
    onPin: (note: PatientNote) => void;
}

/**
 * Every note renders in one calm neutral tone regardless of its internal
 * `severity` — that field is an ODO-team triage signal and must never leak
 * a "critical" tint to a doctor. No severity pill, no tinted border/bg.
 */
function NoteCard({ note, onEdit, onDelete, onPin }: NoteCardProps) {
    const canManage = canManageNote(note);
    const dateLabel =
        note.updatedAt !== note.createdAt
            ? `Updated ${formatCreatedDate(note.updatedAt)}`
            : formatCreatedDate(note.createdAt);

    const cardToneClasses = note.pinned
        ? 'border-yellow-200 bg-yellow-50'
        : 'border-blue-200 bg-blue-50';
    const dividerToneClasses = note.pinned ? 'border-yellow-200' : 'border-blue-200';

    return (
        <div
            className={`rounded-xl border px-4 py-3.5 transition-colors duration-200 ${cardToneClasses}`}
        >
            <div className="flex items-start gap-3 mb-1.5">
                <span className="flex-1 min-w-0 text-[13px] font-bold text-gray-800 font-inter break-words">
                    {note.title}
                </span>

                {canManage && (
                    <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                            type="button"
                            onClick={() => onPin(note)}
                            className={`p-1.5 rounded-lg border transition-colors duration-200 ${
                                note.pinned
                                    ? 'border-yellow-300 bg-yellow-100 text-yellow-700'
                                    : 'border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-gray-700'
                            }`}
                            aria-label={note.pinned ? 'Unpin note' : 'Pin note'}
                            aria-pressed={note.pinned}
                            title={note.pinned ? 'Unpin note' : 'Pin note'}
                        >
                            <PinIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => onEdit(note)}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-500 hover:bg-gray-100 hover:text-gray-800 transition-colors duration-200"
                            aria-label="Edit note"
                        >
                            <PencilIcon className="w-3.5 h-3.5" />
                        </button>
                        <button
                            type="button"
                            onClick={() => onDelete(note)}
                            className="p-1.5 rounded-lg border border-gray-200 text-gray-400 hover:bg-gray-100 hover:text-red-600 transition-colors duration-200"
                            aria-label="Delete note"
                        >
                            <TrashIcon className="w-3.5 h-3.5" />
                        </button>
                    </div>
                )}
            </div>

            {note.pinned && (
                <div className="flex items-center gap-1.5 mb-2">
                    <span className="inline-flex items-center rounded-full bg-yellow-100 text-yellow-800 text-[10px] font-semibold px-2 py-0.5 font-inter">
                        Pinned
                    </span>
                </div>
            )}

            <NoteBody note={note} />

            <div className={`flex items-center gap-2 mt-2.5 pt-2.5 border-t ${dividerToneClasses}`}>
                <div className="w-[18px] h-[18px] flex-shrink-0">
                    <Avatar user={{ name: note.authorName, email: '' }} />
                </div>
                <span className="text-xs font-semibold text-gray-700 font-inter">
                    {note.authorName}
                </span>
                <span className="text-xs text-gray-400 font-inter ml-auto">{dateLabel}</span>
            </div>
        </div>
    );
}

export { NoteCard };
